import { BUCKET_STATIC_URL } from 'src/general/constants/assets';
/**
 * ⚠️ This file contains a number of variables that should probably not be used
 * directly. Instead you should look at the utils in
 * `apps/laliga/src/modules/teams/utils.ts`
 */

/**
 * Logos are stored in a google bucket. You'll need LiveOps help to update
 */
export const TEAM_LOGO_BUCKET_URL = `${BUCKET_STATIC_URL}/team-logos/`;

/**
 * @note These consts are for readability when updating TEAM_DATA. Liga has no
 * team IDs so we're using the shortName as a unique identifier. If a team
 * changes names we'll need to revisit this.
 */
export enum ShortNames {
  athleticclub = 'Athletic Club',
  atleticodemadrid = 'Atlético de Madrid',
  cadizcf = 'Cádiz CF',
  caosasuna = 'CA Osasuna',
  cdleganes = 'CD Leganés',
  cdnumancia = 'CD Numancia',
  cdtenerife = 'CD Tenerife',
  deportivoalaves = 'Deportivo Alavés',
  deportivodelacoruna = 'Deportivo de la Coruña',
  elchecf = 'Elche CF',
  fcbarcelona = 'FC Barcelona',
  getafecf = 'Getafe CF',
  gimnasticdetarragona = 'Gimnàstic de Tarragona',
  gironafc = 'Girona FC',
  granadacf = 'Granada CF',
  hercules = 'Hércules',
  levanteud = 'Levante UD',
  malagacf = 'Málaga CF',
  rayovallecano = 'Rayo Vallecano',
  rccelta = 'RC Celta',
  rcdespanyoldebarcelona = 'RCD Espanyol de Barcelona',
  rcdmallorca = 'RCD Mallorca',
  realbetis = 'Real Betis',
  realmadrid = 'Real Madrid',
  realmurcia = 'Real Murcia',
  realsociedad = 'Real Sociedad',
  realsporting = 'Real Sporting',
  realvalladolidcf = 'Real Valladolid CF',
  realzaragoza = 'Real Zaragoza',
  recreativodehuelva = 'Recreativo de Huelva',
  rracingclub = 'R. Racing Club',
  sdeibar = 'SD Eibar',
  sdhuesca = 'SD Huesca',
  sevillafc = 'Sevilla FC',
  udalmeria = 'UD Almería',
  valenciacf = 'Valencia CF',
  villarrealcf = 'Villarreal CF',
  xerezcd = 'Xerez CD',
}

type ShortName = ShortNames;

/**
 * TEAM_DATA / BRANDING HISTORY by ShortName
 * - The earliest season for a given ShortName will set a baseline for the
 *   branding, additional years will overwrite those properties
 * - A BrandingUpdate requires a `shortName` a `year` and any props to update
 * - Slug will always use the latest slug in the app, it doesn't matter which
 *   year you request
 */

// We need to set a branding baseline that has all of the team's required props
// Presumably more props will come soon
export interface Branding {
  abbreviation: string;
  logoPath: string;
  name: string;
  shortName: ShortName;
  slug: string;
  year: number;
}

// After the intial year we only add changes
export interface BrandingUpdate {
  abbreviation?: string;
  logoPath?: string;
  name?: string;
  shortName?: ShortName;
  slug?: string;
  year: number;
}

type Team = [Branding, ...BrandingUpdate[]];

type Teams = {
  [teamId: string]: Team;
};

export const TEAM_DATA: Teams = {
  [ShortNames.udalmeria]: [
    {
      abbreviation: 'ALM',
      logoPath: '2023',
      name: 'Unión Deportiva Almería SAD',
      shortName: ShortNames.udalmeria,
      slug: 'udalmeria',
      year: 2023,
    },
  ],
  [ShortNames.athleticclub]: [
    {
      abbreviation: 'ATH',
      logoPath: '2023',
      name: 'Athletic Club',
      shortName: ShortNames.athleticclub,
      slug: 'athleticclub',
      year: 2023,
    },
  ],
  [ShortNames.atleticodemadrid]: [
    {
      abbreviation: 'ATM',
      logoPath: '2023',
      name: 'Club Atlético de Madrid SAD',
      shortName: ShortNames.atleticodemadrid,
      slug: 'atleticodemadrid',
      year: 2023,
    },
  ],
  [ShortNames.fcbarcelona]: [
    {
      abbreviation: 'BAR',
      logoPath: '2023',
      name: 'Fútbol Club Barcelona',
      shortName: ShortNames.fcbarcelona,
      slug: 'fcbarcelona',
      year: 2023,
    },
  ],
  [ShortNames.cadizcf]: [
    {
      abbreviation: 'CAD',
      logoPath: '2023',
      name: 'Cádiz Club de Fútbol SAD',
      shortName: ShortNames.cadizcf,
      slug: 'cadizcf',
      year: 2023,
    },
  ],
  [ShortNames.rccelta]: [
    {
      abbreviation: 'CEL',
      logoPath: '2023',
      name: 'Real Club Celta de Vigo SAD',
      shortName: ShortNames.rccelta,
      slug: 'rccelta',
      year: 2023,
    },
  ],
  [ShortNames.elchecf]: [
    {
      abbreviation: 'ELC',
      logoPath: '2023',
      name: 'Elche Club de Fútbol SAD',
      shortName: ShortNames.elchecf,
      slug: 'elchecf',
      year: 2023,
    },
  ],
  [ShortNames.rcdespanyoldebarcelona]: [
    {
      abbreviation: 'ESP',
      logoPath: '2023',
      name: 'Reial Club Deportiu Espanyol de Barcelona SAD',
      shortName: ShortNames.rcdespanyoldebarcelona,
      slug: 'rcdespanyoldebarcelona',
      year: 2023,
    },
  ],
  [ShortNames.getafecf]: [
    {
      abbreviation: 'GET',
      logoPath: '2023',
      name: 'Getafe Club de Fútbol SAD',
      shortName: ShortNames.getafecf,
      slug: 'getafecf',
      year: 2023,
    },
  ],
  [ShortNames.gironafc]: [
    {
      abbreviation: 'GIR',
      logoPath: '2023',
      name: 'Girona Fútbol Club SAD',
      shortName: ShortNames.gironafc,
      slug: 'gironafc',
      year: 2023,
    },
  ],
  [ShortNames.rcdmallorca]: [
    {
      abbreviation: 'MLL',
      logoPath: '2023',
      name: 'Real Club Deportivo Mallorca SAD',
      shortName: ShortNames.rcdmallorca,
      slug: 'rcdmallorca',
      year: 2023,
    },
  ],
  [ShortNames.caosasuna]: [
    {
      abbreviation: 'OSA',
      logoPath: '2023',
      name: 'Club Atlético Osasuna',
      shortName: ShortNames.caosasuna,
      slug: 'caosasuna',
      year: 2023,
    },
  ],
  [ShortNames.cdleganes]: [
    {
      abbreviation: 'LEG',
      logoPath: '2023',
      name: 'Club Deportivo Leganés SAD',
      shortName: ShortNames.cdleganes,
      slug: 'cdleganes',
      year: 2023,
    },
  ],
  [ShortNames.rayovallecano]: [
    {
      abbreviation: 'RAY',
      logoPath: '2023',
      name: 'Rayo Vallecano de Madrid SAD',
      shortName: ShortNames.rayovallecano,
      slug: 'rayovallecano',
      year: 2023,
    },
  ],
  [ShortNames.realbetis]: [
    {
      abbreviation: 'BET',
      logoPath: '2023',
      name: 'Real Betis Balompié SAD',
      shortName: ShortNames.realbetis,
      slug: 'realbetis',
      year: 2023,
    },
  ],
  [ShortNames.realmadrid]: [
    {
      abbreviation: 'RMA',
      logoPath: '2023',
      name: 'Real Madrid Club de Fútbol',
      shortName: ShortNames.realmadrid,
      slug: 'realmadrid',
      year: 2023,
    },
  ],
  [ShortNames.realsociedad]: [
    {
      abbreviation: 'RSO',
      logoPath: '2023',
      name: 'Real Sociedad de Fútbol SAD',
      shortName: ShortNames.realsociedad,
      slug: 'realsociedad',
      year: 2023,
    },
  ],
  [ShortNames.realvalladolidcf]: [
    {
      abbreviation: 'VLL',
      logoPath: '2023',
      name: 'Real Valladolid Club de Fútbol SAD',
      shortName: ShortNames.realvalladolidcf,
      slug: 'realvalladolidcf',
      year: 2023,
    },
  ],
  [ShortNames.sevillafc]: [
    {
      abbreviation: 'SEV',
      logoPath: '2023',
      name: 'Sevilla Fútbol Club SAD',
      shortName: ShortNames.sevillafc,
      slug: 'sevillafc',
      year: 2023,
    },
  ],
  [ShortNames.valenciacf]: [
    {
      abbreviation: 'VAL',
      logoPath: '2023',
      name: 'Valencia Club de Fútbol SAD',
      shortName: ShortNames.valenciacf,
      slug: 'valenciacf',
      year: 2023,
    },
  ],
  [ShortNames.villarrealcf]: [
    {
      abbreviation: 'VIL',
      logoPath: '2023',
      name: 'Villarreal Club de Fútbol SAD',
      shortName: ShortNames.villarrealcf,
      slug: 'villarrealcf',
      year: 2023,
    },
  ],
  [ShortNames.levanteud]: [
    {
      abbreviation: 'LEV',
      logoPath: '2023',
      name: 'Levante Unión Deportiva SAD',
      shortName: ShortNames.levanteud,
      slug: 'levanteud',
      year: 2023,
    },
  ],
  [ShortNames.granadacf]: [
    {
      abbreviation: 'GRA',
      logoPath: '2023',
      name: 'Granada Club de Fútbol SAD',
      shortName: ShortNames.granadacf,
      slug: 'granadacf',
      year: 2023,
    },
  ],
  [ShortNames.deportivoalaves]: [
    {
      abbreviation: 'ALA',
      logoPath: '2023',
      name: 'Deportivo Alavés SAD',
      shortName: ShortNames.deportivoalaves,
      slug: 'deportivoalaves',
      year: 2023,
    },
  ],
  [ShortNames.deportivodelacoruna]: [
    {
      abbreviation: 'DEP',
      logoPath: '2023',
      name: 'Real Club Deportivo de La Coruña S.A.D.',
      shortName: ShortNames.deportivodelacoruna,
      slug: 'deportivodelacoruna',
      year: 2023,
    },
  ],
  [ShortNames.hercules]: [
    {
      abbreviation: 'HER',
      logoPath: '2023',
      name: 'Hércules CF',
      shortName: ShortNames.hercules,
      slug: 'hercules',
      year: 2023,
    },
  ],
  [ShortNames.malagacf]: [
    {
      abbreviation: 'MGA',
      logoPath: '2023',
      name: 'Málaga Club de Fútbol S.A.D.',
      shortName: ShortNames.malagacf,
      slug: 'malagacf',
      year: 2023,
    },
  ],
  [ShortNames.sdeibar]: [
    {
      abbreviation: 'EIB',
      logoPath: '2023',
      name: 'Sociedad Deportiva Eibar SAD',
      shortName: ShortNames.sdeibar,
      slug: 'sdeibar',
      year: 2023,
    },
  ],
  [ShortNames.sdhuesca]: [
    {
      abbreviation: 'HUE',
      logoPath: '2023',
      name: 'Sociedad Deportiva Huesca SAD',
      shortName: ShortNames.sdhuesca,
      slug: 'sdhuesca',
      year: 2023,
    },
  ],
  [ShortNames.rracingclub]: [
    {
      abbreviation: 'RAC',
      logoPath: '2023',
      name: 'Real Racing Club de Santander S.A.D.',
      shortName: ShortNames.rracingclub,
      slug: 'rracingclub',
      year: 2023,
    },
  ],
  [ShortNames.realzaragoza]: [
    {
      abbreviation: 'ZAR',
      logoPath: '2023',
      name: 'Real Zaragoza S.A.D.',
      shortName: ShortNames.realzaragoza,
      slug: 'realzaragoza',
      year: 2023,
    },
  ],
  [ShortNames.gimnasticdetarragona]: [
    {
      abbreviation: 'NAS',
      logoPath: '2023',
      name: 'Club Gimnàstic de Tarragona S.A.E.',
      shortName: ShortNames.gimnasticdetarragona,
      slug: 'gimnasticdetarragona',
      year: 2023,
    },
  ],
  [ShortNames.recreativodehuelva]: [
    {
      abbreviation: 'REC',
      logoPath: '2023',
      name: 'Real Club Recreativo de Huelva',
      shortName: ShortNames.recreativodehuelva,
      slug: 'recreativodehuelva',
      year: 2023,
    },
  ],
  [ShortNames.realmurcia]: [
    {
      abbreviation: 'MUR',
      logoPath: '2023',
      name: 'Real Murcia Club de Fútbol S.A.D.',
      shortName: ShortNames.realmurcia,
      slug: 'realmurcia',
      year: 2023,
    },
  ],
  [ShortNames.realsporting]: [
    {
      abbreviation: 'SPO',
      logoPath: '2023',
      name: 'Real Sporting de Gijón SAD',
      shortName: ShortNames.realsporting,
      slug: 'realsporting',
      year: 2023,
    },
  ],
  [ShortNames.cdnumancia]: [
    {
      abbreviation: 'NUM',
      logoPath: '2023',
      name: 'Club Deportivo Numancia de Soria SAD',
      shortName: ShortNames.cdnumancia,
      slug: 'cdnumancia',
      year: 2023,
    },
  ],
  [ShortNames.cdtenerife]: [
    {
      abbreviation: 'TEN',
      logoPath: '2023',
      name: 'Club Deportivo Tenerife S.A.D.',
      shortName: ShortNames.cdtenerife,
      slug: 'cdtenerife',
      year: 2023,
    },
  ],
  [ShortNames.xerezcd]: [
    {
      abbreviation: 'XER',
      logoPath: '2023',
      name: 'Xerez Club Deportivo',
      shortName: ShortNames.xerezcd,
      slug: 'xerezcd',
      year: 2023,
    },
  ],
};
/**
 * CURRENT_SEASON needs to be updated whenever we add newer seasons
 */
export const CURRENT_SEASON = '2022-2023';
